import type { CalculatedMeasure, FormulaJaql } from '@sisense/sdk-data';
import { translateSharedFormulas } from '@sisense/sdk-data';
import { HttpClient } from '@sisense/sdk-rest-client';
import { getHttpClient } from '@/singleton-clients';

export const FORMULAS_API_URL_PATH = 'api/v1/formulas';

/**
 * Fetch all shared formulas for a data source from the Sisense instance.
 * Returns them as CSDK CalculatedMeasure array.
 *
 * @param dataSourceTitle - Data source title in the Sisense instance
 * @param httpClient - Optional HTTP client; uses singleton if not provided
 * @returns Promise of CalculatedMeasure array for the data source
 * @throws Error if the request fails or translation fails (message includes data source title)
 */
export async function getFormulasByDataSource(
	dataSourceTitle: string,
	httpClient?: HttpClient,
): Promise<CalculatedMeasure[]> {
	const client = httpClient ?? getHttpClient();

	try {
		const url = `${FORMULAS_API_URL_PATH}?datasource=${encodeURIComponent(
			dataSourceTitle,
		)}&flat=true`;
		const formulas = await client.get<FormulaJaql[]>(url, undefined, {
			skipTrackingParam: true,
		});

		if (!formulas || !Array.isArray(formulas)) {
			return [];
		}

		return translateSharedFormulas(formulas);
	} catch (err) {
		const message = err instanceof Error ? err.message : 'Unknown error';
		throw new Error(
			`Failed to fetch formulas for data source: ${dataSourceTitle}. ${message}`,
			{ cause: err },
		);
	}
}
